import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { siteNames, createUrlResolver, getLanguageFromLocale } from "@seek/melways-sites";
import { useTranslations, useLanguage } from "@vocab/react";
import { Box, PageBlock, Text, Strong, Hidden, TextLink, IconCritical, TextLinkButton, IconCaution, Stack } from "braid-design-system";
import * as React from "react";
import { defaultPermissions, getAuthenticationStatus, isUnauthenticated, isAuthenticated, DefaultLinkRenderer } from "../EmployerFooter/EmployerFooter.mjs";
import * as Apollo from "@apollo/client";
import { gql } from "@apollo/client";
import { translations as translations$3 } from "../../EmployerHeader/.vocab/index.mjs";
import { BrandedLogo } from "./components/BrandedLogo/BrandedLogo.mjs";
import { EMPLOYER_HELP_CENTRE_URL } from "@seek/shared-constants";
import { translations } from "../../EmployerHeader/components/CreditHoldAlert/.vocab/index.mjs";
import { HeaderActions } from "./components/HeaderActions/HeaderActions.mjs";
import { translations as translations$1 } from "../../EmployerHeader/components/MaintenanceBanner/.vocab/index.mjs";
import { MobileMenuDropdown } from "./components/MobileMenuDropdown/MobileMenuDropdown.mjs";
import { DesktopNavigation } from "./components/Navigation/DesktopNavigation.mjs";
import { DesktopSubNavigation } from "./components/Navigation/DesktopSubNavigation.mjs";
import * as pageUrl from "@seek/adv-constants";
import { analyticsDesktopHeaderEvents, analyticsEvents, filterLinksOnSiteAndHref } from "../EmployerFooter/AuthenticatedFooterLinks/AuthenticatedFooterLinks.mjs";
import { translations as translations$2 } from "../../EmployerHeader/components/Navigation/.vocab/index.mjs";
import { ScreenReaderSkipLink } from "./components/ScreenReaderHelpers/ScreenReaderSkipLink.mjs";
import { ScreenReaderSkipTarget } from "./components/ScreenReaderHelpers/ScreenReaderSkipTarget.mjs";
import { root, logoContent, actionsContent, divider } from "./EmployerHeader.css.mjs";
const defaultOptions = {};
const CustomerServiceViewUrlDocument = gql`
    query CustomerServiceViewUrl($headerFooterInput: HeaderFooterContextInput) {
  headerFooterContext {
    identityContext {
      act(input: $headerFooterInput) {
        csToggleHref
      }
    }
  }
}
    `;
function useCustomerServiceViewUrlLazyQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery(CustomerServiceViewUrlDocument, options);
}
const TriggerActivationV2Document = gql`
    mutation TriggerActivationV2 {
  triggerActivationV2 {
    success
  }
}
    `;
function useTriggerActivationV2Mutation(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation(TriggerActivationV2Document, options);
}
const defaultLocale = "en-AU";
const defaultSite = siteNames.employer.seek.au;
const defaultValue = {
  authenticationStatus: "pending",
  shouldDisplayActions: true,
  shouldDisplayCtaButton: true,
  hasError: false,
  basic: false,
  isSignedInToAccount: false,
  isAgency: false,
  hasAlert: false,
  advertiserActivationPending: false,
  permissionOptions: defaultPermissions,
  impersonationContext: null,
  site: defaultSite,
  locale: defaultLocale,
  urlResolver: createUrlResolver({
    language: defaultLocale,
    site: defaultSite,
    staging: false
  }),
  handleInteractionTracking: () => {
  }
};
const EmployerHeaderContext = React.createContext(
  defaultValue
);
const useEmployerHeaderContext = () => {
  const context = React.useContext(EmployerHeaderContext);
  if (!context) {
    throw new Error(
      "EmployerHeaderContext must be used within a EmployerHeaderProvider"
    );
  }
  return context;
};
const EmployerHeaderProvider = ({
  children,
  identityContext: identity,
  user,
  advertiser,
  basic,
  error,
  site,
  locale,
  urlResolver,
  handleInteractionTracking
}) => {
  const authenticationStatus = getAuthenticationStatus(identity);
  const identityIsUnauthenticated = isUnauthenticated(authenticationStatus);
  const isSignedInToAccount = Boolean(advertiser);
  const isAgency = Boolean(advertiser == null ? void 0 : advertiser.isAgency);
  const permissionOptions = (advertiser == null ? void 0 : advertiser.permissionOptions) ?? defaultValue.permissionOptions;
  const showOnCreditHoldMessage = Boolean(advertiser == null ? void 0 : advertiser.showOnCreditHoldMessage);
  const shouldDisplayActions = identityIsUnauthenticated && !isAgency;
  const shouldDisplayCtaButton = (identityIsUnauthenticated || permissionOptions.canCreateJobs) && !isAgency;
  const hasError = typeof error !== "undefined";
  const name = user == null ? void 0 : user.name;
  const advertiserName = advertiser == null ? void 0 : advertiser.name;
  const dropDownLinkText = advertiserName || (user == null ? void 0 : user.firstName) || (identity == null ? void 0 : identity.email.split("@")[0]);
  const advertiserActivationPending = (advertiser == null ? void 0 : advertiser.isActivationPending) || false;
  const billingId = advertiser == null ? void 0 : advertiser.billingId;
  let resolvedSwitchAccountsHref;
  if (typeof (advertiser == null ? void 0 : advertiser.switchAccountsHref) === "string") {
    resolvedSwitchAccountsHref = urlResolver({
      path: advertiser == null ? void 0 : advertiser.switchAccountsHref
    });
  }
  const switchAccountsHref = resolvedSwitchAccountsHref;
  let impersonationContext = null;
  if (identity == null ? void 0 : identity.act) {
    let additionalInfo;
    if (advertiserName) {
      additionalInfo = `${advertiserName} ${billingId}`;
    }
    impersonationContext = {
      ...identity.act,
      subjectEmail: identity.email,
      additionalInfo
    };
  }
  const hasAlert = impersonationContext !== null || showOnCreditHoldMessage;
  return /* @__PURE__ */ jsx(
    EmployerHeaderContext.Provider,
    {
      value: {
        urlResolver,
        site,
        locale,
        authenticationStatus,
        shouldDisplayActions,
        shouldDisplayCtaButton,
        hasError,
        basic,
        isSignedInToAccount,
        isAgency,
        hasAlert,
        permissionOptions,
        name,
        advertiserName,
        dropDownLinkText,
        advertiserActivationPending,
        billingId,
        switchAccountsHref,
        impersonationContext,
        handleInteractionTracking
      },
      children
    }
  );
};
const testTags = {
  // Header menu items
  HOME: "home-page",
  JOBS: "jobs-page",
  TALENT_SEARCH: "talent-search-page",
  PRODUCTS: "products-page",
  HIRING_ADVICE: "hiring-advice",
  MARKET_INSIGHTS: "market-insights",
  ANALYTICS: "analytics",
  // Jobs sub menu items
  JOBS_OPEN: "jobs-open-page",
  JOBS_EXPIRED: "jobs-expired-page",
  JOBS_DRAFT: "jobs-draft-page",
  JOBS_MANAGE_CANDIDATES: "jobs-manage-candidates-page",
  // Talent search sub menu items
  TALENT_SEARCH_PREMIUM: "talent-search-premium-page",
  TALENT_SEARCH_MATCHES: "talent-search-candidate-matches-page",
  TALENT_SEARCH_POOLS: "talent-search-pools-page",
  // Products sub menu items
  PRODUCTS_HUB: "products-hub",
  PRODUCTS_JOB_ADS: "job-ads-page",
  PRODUCTS_DISCOUNTS: "discounts-page",
  PRODUCTS_CANDIDATE_MANAGEMENT: "candidate-management-page",
  PRODUCTS_TALENT_SEARCH: "talent-search-page",
  PRODUCTS_BRANDING: "branding-page",
  PRODUCTS_COMPANY_PROFILES: "company-profiles-page",
  PRODUCTS_RECRUITER_PROFILES: "recruiter-profiles-page",
  HIRING_ADVICE_ATTRACTING_CANDIDATES: "attracting-candidates-page",
  HIRING_ADVICE_HIRING_PROCESS: "hiring-process-page",
  HIRING_ADVICE_ENGAGING_WORKFORCE: "engaging-workforce-page",
  HIRING_ADVICE_HOW_TO: "how-to-page",
  MARKET_INSIGHTS_EMPLOYMENT_SALARY_TRENDS: "employment-salary-trends-page",
  MARKET_INSIGHTS_LAWS_OF_ATTRACTION: "laws-of-attraction-page",
  MARKET_INSIGHTS_CANDIDATE_INSIGHTS: "candidate-insights-page",
  MARKET_INSIGHTS_TALENT_ATTRACTION_LAB: "talent-attraction-lab-page",
  MARKET_INSIGHTS_WORKFORCE_INSIGHTS: "workforce-insights-page",
  MARKET_INSIGHTS_REPORTS: "reports-page",
  // Analytics sub menu items
  ANALYTICS_AD_USAGE: "ad-usage-page",
  ANALYTICS_TALENT_SEARCH_USAGE: "talent-search-usage-page",
  ANALYTICS_ROLE_INSIGHTS: "role-insights-page",
  ACCOUNT_DETAILS: "account-details-page",
  YOUR_TEAM: "users-page",
  PAYABLE_INVOICES: "payable-invoices-page",
  INVOICE_HISTORY: "invoice-history-page",
  COMPANY_PROFILE: "company-profile-page",
  LOGOS: "logos-page",
  AD_PRICE_LOOKUP: "ad-price-lookup-page",
  CONTACT_US: "contact-us-page",
  SIGN_OUT: "sign-out-page",
  // Sign in and registation
  SIGN_IN: "sign-in-page",
  REGISTER: "register-page",
  CREATE_A_JOB_AD: "create-a-job-ad-button",
  LOGO: "seek-logo"
};
const CreditHoldAlert = ({ site }) => {
  const { t } = useTranslations(translations);
  const href = EMPLOYER_HELP_CENTRE_URL[site];
  return /* @__PURE__ */ jsx(Box, { id: "credit-hold-alert", paddingY: "medium", background: "criticalLight", children: /* @__PURE__ */ jsx(PageBlock, { width: "medium", children: /* @__PURE__ */ jsxs(Text, { tone: "critical", icon: /* @__PURE__ */ jsx(IconCritical, {}), children: [
    /* @__PURE__ */ jsx(Strong, { children: t("Overdue account is on hold") }),
    " ",
    /* @__PURE__ */ jsx(Hidden, { above: "mobile", children: /* @__PURE__ */ jsx("br", {}) }),
    /* @__PURE__ */ jsxs(Box, { component: "span", children: [
      t("Account admins"),
      " ",
      t("Visit the Help Centre", {
        TextLink: (children) => /* @__PURE__ */ jsx(TextLink, { href, children })
      })
    ] })
  ] }) }) });
};
const actorDisplayText = (email) => {
  let displayText = "Operator";
  if (email && email.indexOf("@") > -1) {
    displayText = email.substring(0, email.indexOf("@"));
  }
  return displayText;
};
const actAsDisplayText = (email, additionalInfo) => /* @__PURE__ */ jsxs(Fragment, { children: [
  email,
  additionalInfo ? /* @__PURE__ */ jsxs(Fragment, { children: [
    " ",
    /* @__PURE__ */ jsxs("span", { "data-hj-masked": "true", "data-dd-privacy": "mask", children: [
      "(",
      additionalInfo,
      ")"
    ] })
  ] }) : null
] });
const ImpersonationBanner = () => {
  const { impersonationContext: context } = useEmployerHeaderContext();
  const [getCustomerServiceViewUrl] = useCustomerServiceViewUrlLazyQuery();
  if (!context) {
    return null;
  }
  const { operatorEmail, isCsView, additionalInfo, subjectEmail } = context;
  const actor = actorDisplayText(operatorEmail);
  const actAs = actAsDisplayText(subjectEmail, additionalInfo);
  const onCsToggleClick = async () => {
    var _a, _b, _c;
    const location = {
      pathname: "/",
      search: ""
    };
    if (typeof window !== "undefined") {
      location.pathname = window.location.pathname;
      location.search = window.location.search;
    }
    const { data } = await getCustomerServiceViewUrl({
      variables: {
        headerFooterInput: {
          location
        }
      }
    });
    if (((_c = (_b = (_a = data == null ? void 0 : data.headerFooterContext) == null ? void 0 : _a.identityContext) == null ? void 0 : _b.act) == null ? void 0 : _c.csToggleHref) && typeof window !== "undefined") {
      window.location.href = data.headerFooterContext.identityContext.act.csToggleHref;
    }
  };
  return /* @__PURE__ */ jsx(Box, { background: "infoLight", paddingY: "medium", children: /* @__PURE__ */ jsx(PageBlock, { width: "full", children: /* @__PURE__ */ jsxs(Text, { size: "small", align: "center", children: [
    /* @__PURE__ */ jsx(Strong, { data: { "dd-privacy": "mask", "hj-masked": true }, children: actor }),
    " ",
    "is impersonating ",
    actAs,
    ".",
    " ",
    /* @__PURE__ */ jsx(TextLinkButton, { onClick: onCsToggleClick, children: isCsView ? "Switch to user view" : "Switch to CS view" })
  ] }) }) });
};
const BannerWrapper = ({ children }) => /* @__PURE__ */ jsx(Box, { background: "cautionLight", paddingY: "medium", children: /* @__PURE__ */ jsx(PageBlock, { width: "full", children: /* @__PURE__ */ jsx(Text, { size: "small", align: "left", icon: /* @__PURE__ */ jsx(IconCaution, {}), children }) }) });
const MaintenanceBanner = ({ locale }) => {
  const { t } = useTranslations(translations$1);
  const { language } = useLanguage();
  if (language.startsWith("id")) {
    return /* @__PURE__ */ jsx(BannerWrapper, { children: "Situs kami tidak dapat diakses pada 14 Des 2025, pukul 04.00–07.00 WIB karena perbaikan sistem. Mohon maaf atas ketidaknyamanannya." });
  }
  const userTimeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  const maintenanceStartDate = /* @__PURE__ */ new Date("2025-12-14T08:00:00.000+11:00");
  const maintenanceEndDate = /* @__PURE__ */ new Date("2025-12-14T11:00:00.000+11:00");
  const startDateString = new Intl.DateTimeFormat("en-AU", {
    timeZone: userTimeZone
  }).format(maintenanceStartDate);
  const endDateString = new Intl.DateTimeFormat("en-AU", {
    timeZone: userTimeZone
  }).format(maintenanceEndDate);
  const spansMultipleDates = startDateString !== endDateString;
  const timeFormatter = language.startsWith("th") ? new Intl.DateTimeFormat("th-TH", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: userTimeZone,
    hour12: false
  }) : new Intl.DateTimeFormat("en-AU", {
    hour: "numeric",
    timeZone: userTimeZone,
    hour12: true
  });
  const startTime = timeFormatter.format(maintenanceStartDate);
  const endTime = timeFormatter.format(maintenanceEndDate);
  const maintenanceMessage = spansMultipleDates ? t("planned-outage-message-multi-date", {
    startTime,
    endTime
  }) : t("planned-outage-message", {
    startTime,
    endTime,
    startDay: new Intl.DateTimeFormat(locale, {
      day: "numeric",
      timeZone: userTimeZone
    }).format(maintenanceStartDate),
    dayOfWeek: new Intl.DateTimeFormat(locale, {
      weekday: "long",
      timeZone: userTimeZone
    }).format(maintenanceStartDate)
  });
  return /* @__PURE__ */ jsx(BannerWrapper, { children: maintenanceMessage });
};
const getFilteredHeaderNavLinks = (rawNavItems, site) => {
  const filteredNavItems = rawNavItems.filter(
    (item) => filterLinksOnSiteAndHref(item, site)
  );
  return filteredNavItems.map((item) => {
    if (item.subItems && item.subItems.length > 0) {
      return {
        ...item,
        subItems: item.subItems.filter(
          (subItem) => filterLinksOnSiteAndHref(subItem, site)
        )
      };
    }
    return item;
  });
};
const useHeaderNavItems = (site, urlResolver, permissionOptions) => {
  const { t } = useTranslations(translations$2);
  const navigateToDefaultAnalytics = () => {
    let path;
    if (permissionOptions.canViewManagerReports) {
      path = pageUrl.URL_ANALYTICS_AD_USAGE;
    } else if (permissionOptions.canViewInsightsRoleReport) {
      path = pageUrl.URL_ANALYTICS_ROLE_INSIGHTS;
    } else {
      path = pageUrl.URL_ANALYTICS;
    }
    return urlResolver({ path });
  };
  const authenticatedNavItems = [
    {
      id: "home",
      label: t("Home"),
      href: urlResolver({
        path: pageUrl.URL_DASHBOARD
      }),
      "data-analytics": analyticsEvents.TRACK_HOME,
      "data-test": testTags.HOME,
      analyticsEvent: analyticsDesktopHeaderEvents.home
    },
    {
      id: "jobs",
      label: t("Jobs"),
      href: urlResolver({
        path: pageUrl.URL_LIST_JOBS_OPEN
      }),
      "data-analytics": analyticsEvents.TRACK_LIST_JOBS,
      "data-test": testTags.JOBS,
      analyticsEvent: analyticsDesktopHeaderEvents.jobs,
      rel: "nofollow",
      subItems: [
        {
          id: "open",
          label: t("Open"),
          href: urlResolver({
            path: pageUrl.URL_LIST_JOBS_OPEN
          }),
          "data-analytics": analyticsEvents.TRACK_LIST_JOBS_OPEN,
          "data-test": testTags.JOBS_OPEN,
          analyticsEvent: analyticsDesktopHeaderEvents.jobsOpen,
          rel: "nofollow"
        },
        {
          id: "expired",
          label: t("Expired"),
          href: urlResolver({
            path: pageUrl.URL_LIST_JOBS_EXPIRED
          }),
          "data-analytics": analyticsEvents.TRACK_LIST_JOBS_EXPIRED,
          "data-test": testTags.JOBS_EXPIRED,
          analyticsEvent: analyticsDesktopHeaderEvents.jobsExpired,
          rel: "nofollow"
        },
        {
          id: "draft",
          label: t("Draft"),
          href: urlResolver({
            path: pageUrl.URL_LIST_JOBS_DRAFT
          }),
          "data-analytics": analyticsEvents.TRACK_LIST_JOBS_DRAFT,
          "data-test": testTags.JOBS_DRAFT,
          analyticsEvent: analyticsDesktopHeaderEvents.jobsDraft,
          rel: "nofollow"
        },
        {
          id: "candidates",
          label: t("Past applicants"),
          href: urlResolver({
            path: pageUrl.URL_YOUR_CANDIDATES
          }),
          "data-analytics": analyticsEvents.TRACK_MANAGE_CANDIDATES,
          "data-test": testTags.JOBS_MANAGE_CANDIDATES,
          analyticsEvent: analyticsDesktopHeaderEvents.jobsYourCandidates,
          rel: "nofollow"
        }
      ]
    },
    {
      id: "talentSearch",
      label: t("Talent Search"),
      href: urlResolver({
        path: pageUrl.URL_TALENT_SEARCH
      }),
      "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH,
      "data-test": testTags.TALENT_SEARCH,
      analyticsEvent: analyticsDesktopHeaderEvents.talentSearch,
      rel: "nofollow",
      subItems: permissionOptions.canViewPremiumTalentSearch ? [
        {
          id: "talentSearchPremium",
          label: t("Premium Talent Search"),
          href: urlResolver({
            path: pageUrl.URL_TALENT_SEARCH
          }),
          "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH,
          "data-test": testTags.TALENT_SEARCH_PREMIUM,
          analyticsEvent: analyticsDesktopHeaderEvents.talentSearchRoot,
          rel: "nofollow"
        },
        {
          id: "talentSearchCandidateMatches",
          label: t("Candidate Matches"),
          href: urlResolver({
            path: pageUrl.URL_TALENT_SEARCH_CANDIDATE_MATCHES
          }),
          "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH_MATCHES,
          "data-test": testTags.TALENT_SEARCH_MATCHES,
          analyticsEvent: analyticsDesktopHeaderEvents.talentSearchMatches,
          rel: "nofollow"
        },
        {
          id: "talentSearchPools",
          label: t("Talent Pools"),
          href: urlResolver({
            path: pageUrl.URL_TALENT_SEARCH_POOLS
          }),
          "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH_POOLS,
          "data-test": testTags.TALENT_SEARCH_POOLS,
          analyticsEvent: analyticsDesktopHeaderEvents.talentSearchPools,
          rel: "nofollow"
        }
      ] : []
    },
    {
      id: "products",
      label: t("Products"),
      href: urlResolver({
        path: pageUrl.URL_PRODUCTS_HUB
      }),
      "data-analytics": analyticsEvents.TRACK_PRODUCTS,
      "data-test": testTags.PRODUCTS,
      analyticsEvent: analyticsDesktopHeaderEvents.products,
      subItems: [
        {
          id: "whatsNew",
          label: t("What’s new"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_HUB
          }),
          "data-analytics": analyticsEvents.TRACK_PRODUCTS_HUB,
          "data-test": testTags.PRODUCTS_HUB,
          analyticsEvent: analyticsDesktopHeaderEvents.productsHubWhatsNew
        },
        {
          id: "jobAds",
          label: t("Job ads"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_JOB_ADS
          }),
          "data-analytics": analyticsEvents.TRACK_JOB_ADS_PRODUCT,
          "data-test": testTags.PRODUCTS_JOB_ADS,
          analyticsEvent: analyticsDesktopHeaderEvents.productsJobAds
        },
        {
          id: "discounts",
          label: t("Discounts"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_AD_BUDGETS
          }),
          "data-analytics": analyticsEvents.TRACK_DISCOUNTS_PRODUCT,
          "data-test": testTags.PRODUCTS_DISCOUNTS,
          analyticsEvent: analyticsDesktopHeaderEvents.productsDiscounts
        },
        {
          id: "candidateManagement",
          label: t("Candidate management"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_CANDIDATE_MANAGEMENT
          }),
          "data-analytics": analyticsEvents.TRACK_CANDIDATE_MANAGEMENT_PRODUCT,
          "data-test": testTags.PRODUCTS_CANDIDATE_MANAGEMENT,
          analyticsEvent: analyticsDesktopHeaderEvents.productsCandidateManagement
        },
        {
          id: "talentSearchProduct",
          label: t("Talent Search"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_TALENT_SEARCH
          }),
          "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH_PRODUCT,
          "data-test": testTags.PRODUCTS_TALENT_SEARCH,
          analyticsEvent: analyticsDesktopHeaderEvents.productsTalentSearch
        },
        {
          id: "branding",
          label: t("Branding"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_BRANDING
          }),
          "data-analytics": analyticsEvents.TRACK_BRANDING_PRODUCT,
          "data-test": testTags.PRODUCTS_BRANDING,
          analyticsEvent: analyticsDesktopHeaderEvents.productsBranding
        },
        {
          id: "companyProfiles",
          label: t("Company profiles"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_COMPANY_PROFILES
          }),
          "data-analytics": analyticsEvents.TRACK_COMPANY_PROFILES_PRODUCT,
          "data-test": testTags.PRODUCTS_COMPANY_PROFILES,
          analyticsEvent: analyticsDesktopHeaderEvents.productsCompanyProfiles
        },
        {
          id: "recruiterProfiles",
          label: t("Recruiter profiles"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_RECRUITER_PROFILES
          }),
          "data-analytics": analyticsEvents.TRACK_RECRUITER_PROFILES_PRODUCT,
          "data-test": testTags.PRODUCTS_RECRUITER_PROFILES,
          analyticsEvent: analyticsDesktopHeaderEvents.productsRecruiterProfiles,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        }
      ]
    },
    {
      id: "hiringAdvice",
      label: t("Hiring Advice"),
      href: urlResolver({
        path: pageUrl.URL_BLOG_HIRING_ADVICE_ROOT
      }),
      "data-analytics": analyticsEvents.TRACK_BLOG_HIRING_ADVICE,
      "data-test": testTags.HIRING_ADVICE,
      analyticsEvent: analyticsDesktopHeaderEvents.hiringAdvice,
      subItems: [
        {
          id: "attractingCandidates",
          label: t("Attracting candidates"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_ATTRACTING_CANDIDATES
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_ATTRACTING_CANDIDATES,
          "data-test": testTags.HIRING_ADVICE_ATTRACTING_CANDIDATES,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceAttractingCandidates,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "hiringProcess",
          label: t("Hiring process"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_HIRING_PROCESS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_HIRING_PROCESS,
          "data-test": testTags.HIRING_ADVICE_HIRING_PROCESS,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceHiringProcess,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "engagingYourWorkforce",
          label: t("Engaging your workforce"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_ENGAGING_WORKFORCE
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_ENGAGING_WORKFORCE,
          "data-test": testTags.HIRING_ADVICE_ENGAGING_WORKFORCE,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceEngagingYourWorkforce,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "howTo",
          label: t("How-To"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_HOW_TO_GET_MOST_SEEK
          }),
          "data-analytics": analyticsEvents.TRACK_HOW_TO,
          "data-test": testTags.HIRING_ADVICE_HOW_TO,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceHowTo,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        }
      ]
    },
    {
      id: "marketInsights",
      label: t("Market Insights"),
      href: urlResolver({
        path: pageUrl.URL_BLOG_MARKET_INSIGHTS_ROOT
      }),
      "data-analytics": analyticsEvents.TRACK_BLOG_MARKET_INSIGHTS,
      "data-test": testTags.MARKET_INSIGHTS,
      analyticsEvent: analyticsDesktopHeaderEvents.marketInsights,
      subItems: [
        {
          id: "employmentAndSalaryTrends",
          label: t("Employment and salary trends"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_EMPLOYMENT_SALARY_TRENDS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_EMPLOYMENT_SALARY_TRENDS,
          "data-test": testTags.MARKET_INSIGHTS_EMPLOYMENT_SALARY_TRENDS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsEmploymentAndSalaryTrends
        },
        {
          id: "candidateInsights",
          label: t("Candidate insights"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_CANDIDATE_INSIGHTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CANDIDATE_INSIGHTS,
          "data-test": testTags.MARKET_INSIGHTS_CANDIDATE_INSIGHTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsCandidateInsights,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        },
        {
          id: "talentAttractionLab",
          label: t("Talent Attraction Lab"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_TALENT_ATTRACTION_LAB
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_TALENT_ATTRACTION_LAB,
          "data-test": testTags.MARKET_INSIGHTS_TALENT_ATTRACTION_LAB,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsTalentAttractionLab,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.id,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "workforceInsights",
          label: t("Workforce insights"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_WORKFORCE_INSIGHTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_WORKFORCE_INSIGHTS,
          "data-test": testTags.MARKET_INSIGHTS_WORKFORCE_INSIGHTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsWorkforceInsights,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "lawsOfAttraction",
          label: "Laws of Attraction",
          href: urlResolver({
            path: pageUrl.URL_LAWS_OF_ATTRACTION,
            product: "candidate"
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_LAWS_OF_ATTRACTION,
          "data-test": testTags.MARKET_INSIGHTS_LAWS_OF_ATTRACTION,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsLawsOfAttraction,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        },
        {
          id: "reports",
          label: t("Reports"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_REPORTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_REPORTS,
          "data-test": testTags.MARKET_INSIGHTS_REPORTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsReports,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th,
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        }
      ]
    },
    {
      id: "analytics",
      label: t("Analytics"),
      href: navigateToDefaultAnalytics(),
      "data-analytics": analyticsEvents.TRACK_ANALYTICS,
      "data-test": testTags.ANALYTICS,
      analyticsEvent: analyticsDesktopHeaderEvents.analytics,
      subItems: [
        {
          id: "adUsage",
          label: t("Ad usage"),
          href: urlResolver({
            path: pageUrl.URL_ANALYTICS_AD_USAGE
          }),
          "data-analytics": analyticsEvents.TRACK_ANALYTICS_AD_USAGE,
          "data-test": testTags.ANALYTICS_AD_USAGE,
          analyticsEvent: analyticsDesktopHeaderEvents.analyticsAdUsageReport
        },
        {
          id: "talentSearchUsage",
          label: t("Talent Search usage"),
          href: urlResolver({
            path: pageUrl.URL_ANALYTICS_TALENT_SEARCH_USAGE
          }),
          "data-analytics": analyticsEvents.TRACK_ANALYTICS_TALENT_SEARCH_USAGE,
          "data-test": testTags.ANALYTICS_TALENT_SEARCH_USAGE,
          analyticsEvent: analyticsDesktopHeaderEvents.analyticsTalentSearchUsageReport
        },
        {
          id: "roleInsights",
          label: t("Role Insights"),
          href: urlResolver({
            path: pageUrl.URL_ANALYTICS_ROLE_INSIGHTS
          }),
          isBeta: true,
          "data-analytics": analyticsEvents.TRACK_ANALYTICS_ROLE_INSIGHTS,
          "data-test": testTags.ANALYTICS_ROLE_INSIGHTS,
          analyticsEvent: analyticsDesktopHeaderEvents.analyticsRoleInsights,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        }
      ]
    }
  ];
  const unauthenticatedNavItems = [
    {
      id: "home",
      label: t("Home"),
      href: urlResolver({
        path: pageUrl.URL_ROOT
      }),
      "data-analytics": analyticsEvents.TRACK_HOME,
      "data-test": testTags.HOME,
      analyticsEvent: analyticsDesktopHeaderEvents.home
    },
    {
      id: "jobs",
      label: t("Jobs"),
      href: urlResolver({
        path: pageUrl.URL_PRODUCTS_JOB_ADS
      }),
      "data-analytics": analyticsEvents.TRACK_JOB_ADS_PRODUCT,
      "data-test": testTags.PRODUCTS_JOB_ADS,
      analyticsEvent: analyticsDesktopHeaderEvents.productsTalentSearch,
      rel: "nofollow"
    },
    {
      id: "talentSearch",
      label: t("Talent Search"),
      href: urlResolver({
        path: pageUrl.URL_PRODUCTS_TALENT_SEARCH
      }),
      "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH_PRODUCT,
      "data-test": testTags.PRODUCTS_TALENT_SEARCH,
      analyticsEvent: analyticsDesktopHeaderEvents.productsTalentSearch,
      rel: "nofollow"
    },
    {
      id: "products",
      label: t("Products"),
      href: urlResolver({
        path: pageUrl.URL_PRODUCTS_HUB
      }),
      "data-analytics": analyticsEvents.TRACK_PRODUCTS,
      "data-test": testTags.PRODUCTS,
      analyticsEvent: analyticsDesktopHeaderEvents.products,
      subItems: [
        {
          id: "whatsNew",
          label: t("What’s new"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_HUB
          }),
          "data-analytics": analyticsEvents.TRACK_PRODUCTS_HUB,
          "data-test": testTags.PRODUCTS_HUB,
          analyticsEvent: analyticsDesktopHeaderEvents.productsHubWhatsNew
        },
        {
          id: "jobAds",
          label: t("Job ads"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_JOB_ADS
          }),
          "data-analytics": analyticsEvents.TRACK_JOB_ADS_PRODUCT,
          "data-test": testTags.PRODUCTS_JOB_ADS,
          analyticsEvent: analyticsDesktopHeaderEvents.productsJobAds
        },
        {
          id: "discounts",
          label: t("Discounts"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_AD_BUDGETS
          }),
          "data-analytics": analyticsEvents.TRACK_DISCOUNTS_PRODUCT,
          "data-test": testTags.PRODUCTS_DISCOUNTS,
          analyticsEvent: analyticsDesktopHeaderEvents.productsDiscounts
        },
        {
          id: "candidateManagement",
          label: t("Candidate management"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_CANDIDATE_MANAGEMENT
          }),
          "data-analytics": analyticsEvents.TRACK_CANDIDATE_MANAGEMENT_PRODUCT,
          "data-test": testTags.PRODUCTS_CANDIDATE_MANAGEMENT,
          analyticsEvent: analyticsDesktopHeaderEvents.productsCandidateManagement
        },
        {
          id: "talentSearchProduct",
          label: t("Talent Search"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_TALENT_SEARCH
          }),
          "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH_PRODUCT,
          "data-test": testTags.PRODUCTS_TALENT_SEARCH,
          analyticsEvent: analyticsDesktopHeaderEvents.productsTalentSearch
        },
        {
          id: "branding",
          label: t("Branding"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_BRANDING
          }),
          "data-analytics": analyticsEvents.TRACK_BRANDING_PRODUCT,
          "data-test": testTags.PRODUCTS_BRANDING,
          analyticsEvent: analyticsDesktopHeaderEvents.productsBranding
        },
        {
          id: "companyProfiles",
          label: t("Company profiles"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_COMPANY_PROFILES
          }),
          "data-analytics": analyticsEvents.TRACK_COMPANY_PROFILES_PRODUCT,
          "data-test": testTags.PRODUCTS_COMPANY_PROFILES,
          analyticsEvent: analyticsDesktopHeaderEvents.productsCompanyProfiles
        },
        {
          id: "recruiterProfiles",
          label: t("Recruiter profiles"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_RECRUITER_PROFILES
          }),
          "data-analytics": analyticsEvents.TRACK_RECRUITER_PROFILES_PRODUCT,
          "data-test": testTags.PRODUCTS_RECRUITER_PROFILES,
          analyticsEvent: analyticsDesktopHeaderEvents.productsRecruiterProfiles,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        }
      ]
    },
    {
      id: "hiringAdvice",
      label: t("Hiring Advice"),
      href: urlResolver({
        path: pageUrl.URL_BLOG_HIRING_ADVICE_ROOT
      }),
      "data-analytics": analyticsEvents.TRACK_BLOG_HIRING_ADVICE,
      "data-test": testTags.HIRING_ADVICE,
      analyticsEvent: analyticsDesktopHeaderEvents.hiringAdvice,
      subItems: [
        {
          id: "attractingCandidates",
          label: t("Attracting candidates"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_ATTRACTING_CANDIDATES
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_ATTRACTING_CANDIDATES,
          "data-test": testTags.HIRING_ADVICE_ATTRACTING_CANDIDATES,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceAttractingCandidates,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "hiringProcess",
          label: t("Hiring process"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_HIRING_PROCESS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_HIRING_PROCESS,
          "data-test": testTags.HIRING_ADVICE_HIRING_PROCESS,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceHiringProcess,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "engagingYourWorkforce",
          label: t("Engaging your workforce"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_ENGAGING_WORKFORCE
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_ENGAGING_WORKFORCE,
          "data-test": testTags.HIRING_ADVICE_ENGAGING_WORKFORCE,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceEngagingYourWorkforce,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "howTo",
          label: t("How-To"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_HOW_TO_GET_MOST_SEEK
          }),
          "data-analytics": analyticsEvents.TRACK_HOW_TO,
          "data-test": testTags.HIRING_ADVICE_HOW_TO,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceHowTo,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        }
      ]
    },
    {
      id: "marketInsights",
      label: t("Market Insights"),
      href: urlResolver({
        path: pageUrl.URL_BLOG_MARKET_INSIGHTS_ROOT
      }),
      "data-analytics": analyticsEvents.TRACK_BLOG_MARKET_INSIGHTS,
      "data-test": testTags.MARKET_INSIGHTS,
      analyticsEvent: analyticsDesktopHeaderEvents.marketInsights,
      subItems: [
        {
          id: "employmentAndSalaryTrends",
          label: t("Employment and salary trends"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_EMPLOYMENT_SALARY_TRENDS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_EMPLOYMENT_SALARY_TRENDS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsEmploymentAndSalaryTrends
        },
        {
          id: "candidateInsights",
          label: t("Candidate insights"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_CANDIDATE_INSIGHTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CANDIDATE_INSIGHTS,
          "data-test": testTags.MARKET_INSIGHTS_CANDIDATE_INSIGHTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsCandidateInsights,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        },
        {
          id: "talentAttractionLab",
          label: t("Talent Attraction Lab"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_TALENT_ATTRACTION_LAB
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_TALENT_ATTRACTION_LAB,
          "data-test": testTags.MARKET_INSIGHTS_TALENT_ATTRACTION_LAB,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsTalentAttractionLab,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.id,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "workforceInsights",
          label: t("Workforce insights"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_WORKFORCE_INSIGHTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_WORKFORCE_INSIGHTS,
          "data-test": testTags.MARKET_INSIGHTS_WORKFORCE_INSIGHTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsWorkforceInsights,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "lawsOfAttraction",
          label: "Laws of Attraction",
          href: urlResolver({
            path: pageUrl.URL_LAWS_OF_ATTRACTION,
            product: "candidate"
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_LAWS_OF_ATTRACTION,
          "data-test": testTags.MARKET_INSIGHTS_LAWS_OF_ATTRACTION,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsLawsOfAttraction,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        },
        {
          id: "reports",
          label: t("Reports"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_REPORTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_REPORTS,
          "data-test": testTags.MARKET_INSIGHTS_REPORTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsReports,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th,
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        }
      ]
    },
    {
      id: "analytics",
      label: t("Analytics"),
      href: navigateToDefaultAnalytics(),
      "data-analytics": analyticsEvents.TRACK_ANALYTICS,
      "data-test": testTags.ANALYTICS,
      analyticsEvent: analyticsDesktopHeaderEvents.analytics
    }
  ];
  return {
    unauthenticatedNavItems: getFilteredHeaderNavLinks(
      unauthenticatedNavItems,
      site
    ),
    authenticatedNavItems: getFilteredHeaderNavLinks(
      authenticatedNavItems,
      site
    )
  };
};
const SITE_SKIP_TO_CONTENT = "start-of-content";
const EmployerHeader = ({
  identityContext: identity,
  user,
  advertiser,
  activeTabId,
  activeSubTabId,
  basic = false,
  divider: divider$1 = false,
  error,
  linkRenderer: LinkRenderer = DefaultLinkRenderer,
  onToggleButtonClick = () => {
  },
  onTrackedItemInteraction = () => {
  },
  id,
  locale,
  site,
  isStaging
}) => {
  const { t } = useTranslations(translations$3);
  const urlResolver = createUrlResolver({
    site,
    language: locale,
    staging: isStaging
  });
  const [shouldDisplayMaintenanceBanner, setShouldDisplayMaintenanceBanner] = React.useState(false);
  React.useEffect(() => {
    const urlHasMbParam = typeof window !== "undefined" && window.location.search.includes("mb=true");
    const isWithinMaintenanceWindow = /* @__PURE__ */ new Date() > /* @__PURE__ */ new Date("2025-11-10T00:00:00.000+11:00") && /* @__PURE__ */ new Date() < /* @__PURE__ */ new Date("2025-12-14T11:00:00.000+11:00");
    setShouldDisplayMaintenanceBanner(
      urlHasMbParam || isWithinMaintenanceWindow
    );
  }, []);
  const isAgency = Boolean(advertiser == null ? void 0 : advertiser.isAgency);
  const permissionOptions = (advertiser == null ? void 0 : advertiser.permissionOptions) ?? defaultPermissions;
  const showOnCreditHoldMessage = Boolean(advertiser == null ? void 0 : advertiser.showOnCreditHoldMessage);
  const shouldDisplayDesktopNavigation = !basic && !isAgency;
  const shouldDisplayDesktopSubNavigation = !basic && !isAgency;
  const authenticationStatus = getAuthenticationStatus(identity);
  const { authenticatedNavItems, unauthenticatedNavItems } = useHeaderNavItems(
    site,
    urlResolver,
    permissionOptions
  );
  const identityIsAuthenticated = isAuthenticated(authenticationStatus);
  const allNavItems = identityIsAuthenticated ? authenticatedNavItems : unauthenticatedNavItems;
  const navItems = allNavItems.filter(
    (item) => (
      // @TEMP: 15/04/25 for Talent Search only; Remove when discriminant no longer needed
      (item.id !== "talentSearch" || item.id === "talentSearch" && activeTabId === "talentSearch") && (permissionOptions.canViewManagerReports || permissionOptions.canViewInsightsRoleReport || activeTabId === "analytics" || // This is to make sure that the analytics tab is always displayed when the user views Analytics products
      item.id !== "analytics")
    )
  );
  const activeItem = navItems.find((item) => item.id === activeTabId);
  const subItems = activeItem && activeItem.subItems ? activeItem.subItems : [];
  const analyticsSubItemIds = permissionOptions.canViewManagerReports ? ["adUsage", "talentSearchUsage"] : [];
  if (permissionOptions.canViewInsightsRoleReport) {
    analyticsSubItemIds.push("roleInsights");
  }
  const activeSubItems = (activeItem == null ? void 0 : activeItem.id) === "analytics" ? subItems.filter((item) => analyticsSubItemIds.includes(item.id)) : subItems;
  const mobileNavItems = [...navItems].map(
    (item) => {
      var _a;
      return item.id === "analytics" ? {
        ...item,
        subItems: (_a = item.subItems) == null ? void 0 : _a.filter(
          (subItem) => analyticsSubItemIds.includes(subItem.id)
        )
      } : item;
    }
  );
  const handleItemInteraction = (event) => {
    onTrackedItemInteraction({
      ...event,
      accountPermissions: permissionOptions,
      authenticationStatus,
      isAgency,
      isSwitchAccountEnabled: Boolean(advertiser == null ? void 0 : advertiser.switchAccountsHref),
      isCreditOnHold: showOnCreditHoldMessage
    });
  };
  const [triggerActivation] = useTriggerActivationV2Mutation();
  React.useEffect(() => {
    if (!identityIsAuthenticated) {
      return;
    }
    if (activeTabId !== "home" && activeTabId !== "jobs") {
      return;
    }
    triggerActivation();
  }, [identityIsAuthenticated, activeTabId]);
  return /* @__PURE__ */ jsx(
    EmployerHeaderProvider,
    {
      urlResolver,
      site,
      locale,
      identityContext: identity,
      user,
      advertiser,
      basic,
      error,
      handleInteractionTracking: handleItemInteraction,
      children: /* @__PURE__ */ jsxs(Stack, { space: "none", children: [
        shouldDisplayMaintenanceBanner && /* @__PURE__ */ jsx(MaintenanceBanner, { locale }),
        (identity == null ? void 0 : identity.act) && /* @__PURE__ */ jsx(ImpersonationBanner, {}),
        /* @__PURE__ */ jsxs(
          "header",
          {
            role: "banner",
            "aria-label": t("Site Header"),
            style: { position: "relative" },
            children: [
              /* @__PURE__ */ jsx(ScreenReaderSkipLink, { to: SITE_SKIP_TO_CONTENT, children: t("Skip to content") }),
              /* @__PURE__ */ jsx(
                Box,
                {
                  position: "relative",
                  className: [
                    root,
                    {
                      [divider]: divider$1
                    }
                  ],
                  background: "surface",
                  children: /* @__PURE__ */ jsx(PageBlock, { width: "full", children: /* @__PURE__ */ jsxs(
                    Box,
                    {
                      display: "flex",
                      alignItems: "center",
                      width: "full",
                      flexWrap: "wrap",
                      children: [
                        /* @__PURE__ */ jsx(
                          Box,
                          {
                            display: "flex",
                            flexWrap: "wrap",
                            className: logoContent,
                            paddingRight: "medium",
                            children: /* @__PURE__ */ jsx(BrandedLogo, { linkRenderer: LinkRenderer })
                          }
                        ),
                        /* @__PURE__ */ jsxs(
                          Box,
                          {
                            display: "flex",
                            flexWrap: "wrap",
                            "flex-direction": "row-reverse",
                            flexGrow: 1,
                            children: [
                              shouldDisplayDesktopNavigation && /* @__PURE__ */ jsx(
                                DesktopNavigation,
                                {
                                  items: navItems,
                                  activeTabId,
                                  linkRenderer: LinkRenderer
                                }
                              ),
                              /* @__PURE__ */ jsx(
                                Box,
                                {
                                  display: "flex",
                                  justifyContent: "flexEnd",
                                  className: actionsContent,
                                  children: /* @__PURE__ */ jsx(
                                    HeaderActions,
                                    {
                                      id,
                                      onToggleButtonClick,
                                      linkRenderer: LinkRenderer,
                                      site,
                                      language: getLanguageFromLocale(locale),
                                      identityIsAuthenticated
                                    }
                                  )
                                }
                              )
                            ]
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          MobileMenuDropdown,
                          {
                            id,
                            activeTabId,
                            activeSubTabId,
                            items: mobileNavItems,
                            onToggleButtonClick,
                            linkRenderer: LinkRenderer
                          }
                        )
                      ]
                    }
                  ) })
                }
              ),
              shouldDisplayDesktopSubNavigation && /* @__PURE__ */ jsx(
                DesktopSubNavigation,
                {
                  activeSubTabId,
                  subItems: activeSubItems,
                  linkRenderer: LinkRenderer
                }
              )
            ]
          }
        ),
        showOnCreditHoldMessage && /* @__PURE__ */ jsx(CreditHoldAlert, { site }),
        /* @__PURE__ */ jsx(ScreenReaderSkipTarget, { name: SITE_SKIP_TO_CONTENT })
      ] })
    }
  );
};
export {
  EmployerHeader,
  testTags,
  useEmployerHeaderContext
};
